import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { userHasAnyRole } from "../../../services/authService";
import {
  createClientTag,
  deactivateClientTag,
  getClientTags,
  updateClientTag,
} from "../../../services/clientTagService";
import { ClientTag } from "../../../types/clientTag";
import { PaginationMeta } from "../../../types/api";
import TagFormModal, { TagFormValues } from "./TagFormModal";

const PER_PAGE = 15;

const ClientTagsPage = () => {
  const { t } = useTranslation();
  const canManage = userHasAnyRole(["owner", "manager"]);

  document.title = t("settings.client_tags.pageTitle");

  const [tags, setTags] = useState<ClientTag[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<ClientTag | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<ClientTag | null>(null);
  const [deactivateModal, setDeactivateModal] = useState(false);

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getClientTags({
        page,
        per_page: PER_PAGE,
      });
      setTags(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("settings.client_tags.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const openCreate = () => {
    setEditingTag(null);
    setModalOpen(true);
  };

  const openEdit = (tag: ClientTag) => {
    setEditingTag(tag);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingTag(null);
  };

  const handleSubmit = async (values: TagFormValues) => {
    setSubmitting(true);
    const color = values.color.trim() === "" ? null : values.color.trim();
    const payload = {
      name: values.name.trim(),
      color,
      is_active: values.is_active,
    };

    try {
      if (editingTag) {
        const response = await updateClientTag(editingTag.id, payload);
        toast.success(response.message);
      } else {
        const response = await createClientTag(payload);
        toast.success(response.message);
      }

      closeModal();

      if (!editingTag && page !== 1) {
        setPage(1);
      } else {
        await fetchTags();
      }
    } catch (error: any) {
      toast.error(error?.data || t("settings.client_tags.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) {
      return;
    }

    try {
      const response = await deactivateClientTag(deactivateTarget.id);
      toast.success(response.message);
      setDeactivateModal(false);
      setDeactivateTarget(null);
      await fetchTags();
    } catch (error: any) {
      toast.error(error?.data || t("settings.client_tags.toast.deactivateError"));
    }
  };

  const totalPages = meta?.last_page ?? 1;

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("settings.client_tags.breadcrumb.title")}
            pageTitle={t("settings.client_tags.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                  <div className="flex-grow-1">
                    <h4 className="card-title mb-1">
                      {t("settings.client_tags.card.title")}
                    </h4>
                    <p className="text-muted mb-0 small">
                      {t("settings.client_tags.card.subtitle")}
                    </p>
                  </div>
                  {canManage ? (
                    <Button color="primary" onClick={openCreate}>
                      <i className="ri-add-line align-bottom me-1" />
                      {t("settings.client_tags.actions.new")}
                    </Button>
                  ) : null}
                </CardHeader>

                <CardBody>
                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : tags.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-3">{t("settings.client_tags.empty.noData")}</p>
                      {canManage ? (
                        <Button color="soft-primary" onClick={openCreate}>
                          {t("settings.client_tags.actions.createFirst")}
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table hover className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("settings.client_tags.table.name")}</th>
                              <th style={{ width: 100 }}>
                                {t("settings.client_tags.table.color")}
                              </th>
                              <th style={{ width: 120 }}>
                                {t("settings.client_tags.table.status")}
                              </th>
                              {canManage ? (
                                <th className="text-end" style={{ width: 120 }}>
                                  {t("settings.client_tags.table.actions")}
                                </th>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody>
                            {tags.map((tag) => (
                              <tr key={tag.id}>
                                <td className="fw-medium">{tag.name}</td>
                                <td>
                                  {tag.color ? (
                                    <span
                                      className="d-inline-block rounded"
                                      style={{
                                        width: 20,
                                        height: 20,
                                        backgroundColor: tag.color,
                                        border: "1px solid #dee2e6",
                                      }}
                                      title={tag.color}
                                    />
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td>
                                  <Badge
                                    color={tag.is_active ? "success" : "secondary"}
                                    className="text-uppercase"
                                  >
                                    {tag.is_active
                                      ? t("settings.client_tags.status.active")
                                      : t("settings.client_tags.status.inactive")}
                                  </Badge>
                                </td>
                                {canManage ? (
                                  <td className="text-end">
                                    <Button
                                      color="soft-primary"
                                      size="sm"
                                      className="me-1"
                                      title={t("settings.client_tags.actions.edit")}
                                      onClick={() => openEdit(tag)}
                                    >
                                      <i className="ri-pencil-line" />
                                    </Button>
                                    {tag.is_active ? (
                                      <Button
                                        color="soft-danger"
                                        size="sm"
                                        title={t("settings.client_tags.actions.deactivate")}
                                        onClick={() => {
                                          setDeactivateTarget(tag);
                                          setDeactivateModal(true);
                                        }}
                                      >
                                        <i className="ri-forbid-line" />
                                      </Button>
                                    ) : null}
                                  </td>
                                ) : null}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {meta && totalPages > 1 ? (
                        <Row className="align-items-center mt-3 g-3">
                          <Col sm={6}>
                            <p className="text-muted mb-0">
                              {t("settings.client_tags.pagination.showing", {
                                from: meta.from ?? 0,
                                to: meta.to ?? 0,
                                total: meta.total,
                              })}
                            </p>
                          </Col>
                          <Col sm={6}>
                            <Pagination className="pagination-separated justify-content-sm-end mb-0">
                              <PaginationItem disabled={page <= 1}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page > 1) {
                                      setPage(page - 1);
                                    }
                                  }}
                                >
                                  {t("settings.client_tags.pagination.previous")}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem active>
                                <PaginationLink href="#">
                                  {page} / {totalPages}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem disabled={page >= totalPages}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page < totalPages) {
                                      setPage(page + 1);
                                    }
                                  }}
                                >
                                  {t("settings.client_tags.pagination.next")}
                                </PaginationLink>
                              </PaginationItem>
                            </Pagination>
                          </Col>
                        </Row>
                      ) : null}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <TagFormModal
        isOpen={modalOpen}
        editingTag={editingTag}
        submitting={submitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />

      <Modal isOpen={deactivateModal} toggle={() => setDeactivateModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-forbid-line display-5 text-warning" />
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("settings.client_tags.deactivate.title")}</h4>
              <p className="text-muted mx-4 mb-0">
                {deactivateTarget
                  ? t("settings.client_tags.deactivate.description", {
                      name: deactivateTarget.name,
                    })
                  : t("settings.client_tags.deactivate.fallback")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" onClick={() => setDeactivateModal(false)}>
              {t("settings.client_tags.actions.cancel")}
            </Button>
            <Button color="warning" onClick={confirmDeactivate}>
              {t("settings.client_tags.actions.confirmDeactivate")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ClientTagsPage;
